import React, {createRef, ReactNode, RefObject} from "react";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {PortalGlobals} from "../Portal";
import DomHelper from "../../shared/helper/DomHelper";
import DocumentTableItem from "./DocumentTableItem";
import DocumentPreviewItem from "./DocumentPreviewItem";
import DocumentFilterComponent, {DocumentFilterGlobals} from "../menu/DocumentFilterComponent";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import DocumentFilter from "../document/DocumentFilter";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import {Checkbox} from "primereact/checkbox";
import MessageBox from "../../shared/components/MessageBox";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import Preview from "./Preview";
import Resizeable, {ResizeableDirection} from "../../shared/components/Resizeable";
import {MessageType} from "../../shared/log/MessageInterface";
import ContentMode from "../ContentMode";
import Immutable from "../../shared/immutable/Immutable";
import DocumentSorting, {DocumentSortingGlobals, SortType} from "../document/DocumentSorting";
import {Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../shared/translation/Translate";
import RectSelection from "../document/components/selection/RectSelection";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {isMobile} from "react-device-detect";
import MenuButton from "../../shared/menu/MenuButton";
import {MenuItem} from "primereact/menuitem";
import DocumentSettings from "../document/DocumentSettings";
import SortHelper from "../../shared/helper/SortHelper";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export enum DocumentListModeEnum {
	LIST = "list",
	PREVIEW = "pages"
}

export interface DocumentListGlobals {
	documentListMode: DocumentListModeEnum;
}

export function initDocumentListGlobals(): DocumentListGlobals {
	return {
		documentListMode: DocumentListModeEnum.PREVIEW
	};
}

interface DocumentListProperties extends DocumentFilterGlobals, DocumentSortingGlobals, PortalDocumentManagerGlobals,
	PortalGlobals, I18nGlobals, DocumentListGlobals, DebugGlobals {
	onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
}

interface DocumentListStatus {
	previewSize: string;
	showPreview: boolean;
}

export default class DocumentList extends PortalPureComponent<DocumentListProperties, DocumentListStatus> {
	private previewListElement: RefObject<HTMLDivElement>;

	/**
	 * Constructs a {@link DocumentList} component
	 */
	constructor(props: Readonly<DocumentListProperties>) {
		super(props);

		this.previewListElement = createRef<HTMLDivElement>();
		this.state = {
			showPreview: true,
			previewSize: "25%"
		};

		this.checkSelection = this.checkSelection.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);
	}

	private handleKeydown(event: KeyboardEvent): void {
		if (this.props.contentMode !== ContentMode.STARTSCREEN) {
			return;
		}

		if (event.ctrlKey && event.key.toLowerCase() === "a") {
			event.preventDefault();
			this.props.context.documentManager.selectAll();
		}
	}

	private checkSelection(options: Rectangle | null, event?: MouseEvent): void {
		if (!FunctionHelper.exists(options) || !FunctionHelper.exists(this.previewListElement.current)) {
			return;
		}

		let pageListRect: DOMRect = this.previewListElement.current!.getBoundingClientRect();
		let listItems: Array<HTMLElement> = DomHelper.findAll(this.previewListElement.current, ".list-item");

		for (let listItem of listItems) {
			let pageRect: DOMRect = listItem.getBoundingClientRect();
			let pageX: number = pageRect.x - pageListRect.x;
			let pageY: number = pageRect.y - pageListRect.y;
			let pageWidth: number = pageRect.width;
			let pageHeight: number = pageRect.height;

			let isSelected: boolean = (
					(pageX <= options!.x! + options!.width! &&
						(pageX >= options!.x! || options!.x! + options!.width! <= pageX + pageWidth)
					) ||
					(options!.x! <= pageX + pageWidth &&
						(pageX + pageWidth <= options!.x! + options!.width! || options!.x! >= pageX)
					)
				) &&
				(
					(options!.y! + options!.height! >= pageY &&
						(pageY >= options!.y! || options!.y! + options!.height! <= pageY + pageHeight)
					) ||
					(options!.y! <= pageY + pageHeight &&
						(pageY + pageHeight <= options!.y! + options!.height! || options!.y! >= pageY)
					)
				);

			let documentId: string | null = DomHelper.data(listItem, "id");
			if (documentId === null) {
				continue;
			}

			if (isSelected) {
				this.props.context.documentManager.select(documentId);
			} else {
				if (event?.shiftKey || event?.ctrlKey) {
					continue;
				}

				this.props.context.documentManager.unselect(documentId);
			}
		}
	}

	private setSortBy(type: SortType) {
		let documentSorting: DocumentSorting = DocumentSorting.fromJson(this.props.documentSorting.get());

		documentSorting.setSorting(type);
		this.props.context.getPage().setState({
			documentSorting: Immutable.from(documentSorting.toJson())
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let allSelected: boolean =
			this.props.portalDocuments.get().length === documentManager.getSelection().length;
		let documentFilter: DocumentFilter = DocumentFilter.fromJson(this.props.documentFilter.get());
		let documentSorting: DocumentSorting = DocumentSorting.fromJson(this.props.documentSorting.get());
		let sortedItems: Array<PortalDocumentInterface> = documentSorting.sortItems(this.props.portalDocuments.get());
		let isEmpty: boolean = true;
		let currentDocumentCount: number = 0;

		let selectionGroup: Array<MenuItem> = [];
		let previewItems: Array<ReactNode> = [];
		let tableItems: Array<ReactNode> = [];

		for (let listItem of sortedItems) {
			let portalDocument: PortalDocument;
			let documentSettings: DocumentSettings;

			try {
				portalDocument = documentManager.findDocument(listItem.id!);
				documentSettings = portalDocument.getSettings();
			} catch (e: any) {
				continue;
			}

			let filtered: boolean = documentFilter.filter(portalDocument);

			previewItems.push(
				<DocumentPreviewItem {...this.props} filtered={filtered} key={listItem.id}
									 portalDocument={Immutable.from(listItem)} sortBy={documentSorting.sortBy}/>
			);

			tableItems.push(
				<DocumentTableItem {...this.props} filtered={filtered} key={listItem.id}
								   portalDocument={Immutable.from(listItem)} sortBy={documentSorting.sortBy}/>
			);

			let filetypeGroup: MenuItem | undefined = selectionGroup.find((value) => {
				return value.label === portalDocument.getFileTypeGroup().toLowerCase();
			});
			if (!FunctionHelper.isEmpty(portalDocument.getFileTypeGroup())) {
				if (!FunctionHelper.exists(filetypeGroup)) {
					selectionGroup.push(
						{
							label: portalDocument.getFileTypeGroup().toLowerCase(),
							icon: (documentSettings.selected) ? "icon icon-check" : "icon empty",
							data: documentSettings.selected,
							command: (event) => {
								if (event.item.data) {
									documentManager.clearSelection(portalDocument.getFileTypeGroup());
								} else {
									documentManager.selectAll(portalDocument.getFileTypeGroup());
								}
							}
						}
					)
				} else if (!documentSettings.selected) {
					filetypeGroup!.icon = "icon empty";
					filetypeGroup!.data = false;
				}
			}

			if (!filtered) {
				currentDocumentCount++;
				isEmpty = false;
			}
		}

		selectionGroup = selectionGroup.sort((a: MenuItem, b: MenuItem) => {
			return SortHelper.alphanum(a.label!, b.label!);
		});
		let documentList: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

		let documentListClasses: Array<string> = ["document-list"];
		if (this.props.documentListMode === DocumentListModeEnum.PREVIEW) {
			documentListClasses.push("preview");
		} else {
			documentListClasses.push("table");
		}

		if (isEmpty) {
			documentListClasses.push("empty");
		}

		return (
			<div onDrop={this.props.onDrop} ref={documentList} className={documentListClasses.join(" ")}
				 data-help="global.screen.document.manager" data-position="top">
				<div className="p-grid header">
					<div className="p-col search">
						<div className="p-inputgroup">
							<Button icon="icon icon-close"
									tooltip={Translate.value(
										"startscreen.panels.documentmanager.search.delete", {ns: "screen"}
									)}
									onClick={() => {
										documentFilter.searchValue = "";
										this.props.context.getPage().setState({
											documentFilter: Immutable.from(documentFilter.toJson())
										});
									}}/>
							<InputText type="search" value={documentFilter.searchValue}
									   placeholder={Translate.value(
										   "startscreen.panels.documentmanager.search.placeholder",
										   {ns: "screen"}
									   )}
									   onChange={(event) => {
										   documentFilter.searchValue = (event.target as HTMLInputElement).value;
										   this.props.context.getPage().setState({
											   documentFilter: Immutable.from(documentFilter.toJson())
										   }, () => {
											   for (let listItem of sortedItems) {
												   let portalDocument: PortalDocument =
													   documentManager.findDocument(listItem.id!);

												   if (documentFilter.filter(portalDocument)) {
													   documentManager.unselect(listItem.id!);
												   }
											   }
										   });
									   }}/>
						</div>
					</div>
					<div className="p-col-fixed">
						<Button className="toggle-button" icon="icon"
								onClick={() => this.props.context.getPage().setState({
									documentListMode: this.props.documentListMode === DocumentListModeEnum.LIST ?
										DocumentListModeEnum.PREVIEW : DocumentListModeEnum.LIST
								})}/>
					</div>
				</div>
				<div className="content">
					<table>
						<thead>
						<tr>
							<th className="column column-select">
								<div className="column">
									<Checkbox checked={allSelected}
											  onChange={() => {
												  if (allSelected) {
													  documentManager.clearSelection();
												  } else {
													  documentManager.selectAll();
												  }
											  }}/>
									<MenuButton {...this.props} icon={"icon icon-angle-down"} keepOpen={true}
												disabled={isEmpty || selectionGroup.length === 0}
												menuItems={selectionGroup}/>
								</div>
							</th>
							<th className="column column-name tooltip" data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.NAME)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.name.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<Translate {...this.props}
											   data="startscreen.panels.documentmanager.columns.name.label"
											   namespace="screen"/>
									{documentSorting.renderSortIcon(SortType.NAME)}
								</div>
							</th>
							<th className="column column-archive tooltip" data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.ARCHIVE)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.archive.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<span className="icon icon-archive small"/>
									{documentSorting.renderSortIcon(SortType.ARCHIVE)}
								</div>
							</th>
							<th className="column column-signature tooltip"
								data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.SIGNATURE)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.signature.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<span className="icon icon-signature small"/>
									{documentSorting.renderSortIcon(SortType.SIGNATURE)}
								</div>
							</th>
							<th className="column column-password tooltip" data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.PASSWORD)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.security.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<span className="icon icon-locked"/>
									{documentSorting.renderSortIcon(SortType.PASSWORD)}
								</div>
							</th>
							<th className="column column-attachment tooltip"
								data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.ATTACHMENT)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.attachment.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<span className="icon icon-attachment small"/>
									{documentSorting.renderSortIcon(SortType.ATTACHMENT)}
								</div>
							</th>
							<th className="column column-size tooltip" data-pr-position={"bottom"}
								onClick={() => this.setSortBy(SortType.SIZE)}
								data-pr-tooltip={Translate.value(
									"startscreen.panels.documentmanager.columns.size.tooltip",
									{ns: "screen"}
								)}>
								<div className="column">
									<Translate {...this.props}
											   data="startscreen.panels.documentmanager.columns.size.label"
											   namespace="screen"/>
									{documentSorting.renderSortIcon(SortType.SIZE)}
								</div>
							</th>
							<th className="column-error"/>
							<th className="column column-functions">
								<DocumentFilterComponent {...this.props}/>
							</th>
							<th className="column column-info">
								{currentDocumentCount}/{sortedItems.length}
							</th>
						</tr>
						</thead>
						<tbody>{tableItems}</tbody>
					</table>
					{this.props.documentSelection.get().length === 1 &&
                        <div className="preview-panel" style={this.state.showPreview ? {
							width: this.state.previewSize,
							minWidth: "200px"
						} : undefined}>
                            <Button className="toggle-btn"
                                    icon={"icon" + (this.state.showPreview ? " icon-angle-right" : " icon-angle-left")}
                                    onClick={() => this.setState({
										showPreview: !this.state.showPreview
									})}/>
                            <Resizeable direction={ResizeableDirection.HORIZONTAL} container={documentList}
                                        invert={true}
                                        resizeCallback={(options: { width: string }) => {
											this.setState({
												previewSize: options.width
											});
										}}/>
							{this.state.showPreview &&
                                <div className="table-preview">
                                    <Preview {...this.props} controls={true}
                                             portalDocument={Immutable.from(
												 this.props.documentSelection.get().pop()!
											 )}/>
                                </div>
							}
                        </div>
					}
					<div className="preview-container">
						<div className="preview-list" ref={this.previewListElement}>
							{previewItems}
						</div>
						{FunctionHelper.exists(this.previewListElement.current) && !isMobile &&
                            <RectSelection {...this.props}
                                           startCallback={(values, event) => {
											   if (event?.shiftKey || event?.ctrlKey) {
												   return;
											   }

											   documentManager.clearSelection();
										   }} moveCallback={this.checkSelection}
                                           getParent={() => {
											   return this.previewListElement.current
										   }}/>
						}
					</div>
				</div>
				<MessageBox {...this.props} type={MessageType.INFO} className="empty-message">
					<Translate {...this.props} data="startscreen.panels.documentmanager.empty" namespace="screen"/>
				</MessageBox>
			</div>
		);
	}
}